import {
  ClientApplicationFragment,
  useClientApplicationsQuery,
} from "@graphql/client-applications/ClientApplications.g";
import { TenantedClientApplicationReviewStateChoices } from "@graphql/generated/graphql.g";
import {
  type ColumnDef,
  PaginationState,
  createColumnHelper,
} from "@tanstack/react-table";
import { SortingState } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { ClientApplicationReviewPanel } from "@components/Tables/ClientApplicationsTable/ClientApplicationReviewPanel/ClientApplicationReviewPanel";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Table } from "@design-system/Table/Table";
import { Tag } from "@design-system/Tag/Tag";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";

const PAGE_SIZE = 7;
const SEARCH_DEBOUNCE_TIME = 400;

const columnHelper = createColumnHelper<ClientApplicationFragment>();

type Props = {
  initialPageIndex?: number;
  search?: string;
};

export const ApplicationsUnreviewedTable = ({
  initialPageIndex = 0,
  search,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]); // Add sorting state
  const [selectedApplicationForReview, setSelectedApplicationForReview] =
    useState<ClientApplicationFragment | null>(null);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_TIME);
  const clientApplicationsQuery = useClientApplicationsQuery({
    first: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    reviewState_In: [TenantedClientApplicationReviewStateChoices.Discovered],
    orderBy:
      sorting.length > 0
        ? `${sorting[0].desc ? "-" : ""}${sorting[0].id}`
        : undefined,
    resource_IsPrimaryResource: true,
  });
  const navigate = useNavigate();

  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const isClientApplicationsLoading = clientApplicationsQuery.isLoading;
  const isClientApplicationsFetching = clientApplicationsQuery.isFetching;

  const totalCount =
    clientApplicationsQuery.data?.clientApplications?.totalCount ?? undefined;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
  }, [debouncedSearch]);

  const data = useMemo(() => {
    return (
      clientApplicationsQuery.data?.clientApplications?.edges
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ?.map((edge: any) => edge?.node)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ?.filter((clientApplication: any) => !!clientApplication) ?? []
    );
  }, [clientApplicationsQuery.data]);

  const handleApplicationDashboardClick = useCallback(
    (clientId: string, applicationId: string) => {
      navigate(
        routes.portal.clients.client.applications.application.record.$buildPath(
          {
            params: { clientId, applicationId },
          },
        ),
        {
          viewTransition: true,
        },
      );
    },
    [navigate],
  );

  const handleReviewClick = useCallback(
    (application: ClientApplicationFragment) => {
      setSelectedApplicationForReview(application);
    },
    [],
  );

  const handleCloseReviewPanel = useCallback(() => {
    setSelectedApplicationForReview(null);
  }, []);

  const handleReviewSuccess = useCallback(() => {
    clientApplicationsQuery.refetch();
  }, [clientApplicationsQuery]);

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("resource.productName", {
          header: "Name",
          id: "resource__product_name",
          cell: (props) => {
            return (
              <Clickable
                onClick={() =>
                  handleApplicationDashboardClick(
                    props.row.original.client.id,
                    props.row.original.id,
                  )
                }
                outline="sm"
                stretch
              >
                <Table.CompanyCell
                  name={props.getValue() ?? ""}
                  subtext={props.row.original.resource?.vendorName || undefined}
                  signedLogoUrl={
                    props.row.original.resource?.publicResource
                      ?.signedLogoUrl || undefined
                  }
                  domain={props.row.original.resource?.productDomainName ?? ""}
                />
              </Clickable>
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("client.name", {
          header: "Tenant",
          id: "client__name",
          cell: (props) => {
            return (
              <Table.CompanyCell
                name={props.getValue() ?? ""}
                skipLogo={true}
              />
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("resource.publicResource.category.parent.name", {
          header: "Category",
          enableSorting: false,
          id: "resource__category__parent__name",
          cell: (props) => {
            const parentName = props.getValue();
            const categoryName =
              props.row.original.resource?.publicResource?.category?.name;
            // Fallback to child category name if parent is not available
            const displayName = parentName || categoryName;
            return (
              <Box>
                <Tag color="secondary" text={displayName} />
              </Box>
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("employees.totalCount", {
          header: "Used by",
          id: "rg_total_employees",
          cell: (props) => {
            return <Text>{props.getValue() ?? 0}</Text>;
          },
          footer: undefined,
        }),
        columnHelper.display({
          id: "approvals",
          header: "",
          cell: (props) => {
            return (
              <Box gap={2} justify="center">
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={() => handleReviewClick(props.row.original)}
                  stretch
                >
                  Review
                </Button>
              </Box>
            );
          },
          meta: {
            sticky: true,
            actions: true,
          },
        }),
      ] as ColumnDef<ClientApplicationFragment, string>[],
    [handleApplicationDashboardClick, handleReviewClick],
  );

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <>
      <Table<ClientApplicationFragment, string>
        columns={columns}
        data={data}
        rowCount={totalCount}
        onPaginationChange={handlePaginationChange}
        pagination={pagination}
        isLoading={isClientApplicationsLoading}
        isFetching={isClientApplicationsFetching}
        onPageSizeChange={handlePageSizeChange}
        sorting={sorting}
        showPageSizeDropdown={false}
        onSortingChange={setSorting}
        emptyPlaceholder="All good! Everything is reviewed."
      />

      <ClientApplicationReviewPanel
        application={selectedApplicationForReview}
        clientId={selectedApplicationForReview?.client.id ?? ""}
        isOpen={!!selectedApplicationForReview}
        onClose={handleCloseReviewPanel}
        onSuccess={handleReviewSuccess}
      />
    </>
  );
};
